// src/hooks/useLocalStorageState.ts
// Generic hook: syncs a state value to localStorage by key.
// Supports an optional custom serialiser/deserialiser (defaults to JSON).
import { useCallback, useEffect, useState } from "react";

export function useLocalStorageState<T>(
  key: string,
  defaultValue: T
): [T, (v: T | ((prev: T) => T)) => void] {
  const [value, setValueState] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return defaultValue;
      return JSON.parse(raw) as T;
    } catch {
      return defaultValue;
    }
  });

  const setValue = useCallback(
    (v: T | ((prev: T) => T)) => {
      setValueState((prev) => {
        const next = typeof v === "function" ? (v as (p: T) => T)(prev) : v;
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch {
          // quota exceeded — state still updates in memory
        }
        return next;
      });
    },
    [key]
  );

  // Keep in sync if another tab writes to the same key
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== key) return;
      try {
        const next = e.newValue !== null ? (JSON.parse(e.newValue) as T) : defaultValue;
        setValueState(next);
      } catch {
        // ignore malformed values from other tabs
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return [value, setValue];
}
